import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle } from "@phosphor-icons/react";
import WaiverStep from "@/components/WaiverStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Walk-in registration: the QR on the wall points here. Sign once — never again.
export default function RegisterPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [name, setName] = useState(params.get("name") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [step, setStep] = useState("contact"); // contact | waiver | already | done
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState("");
  const thenUrl = params.get("then") || "";

  const proceed = () => {
    if (thenUrl && thenUrl.startsWith("/")) {
      const sep = thenUrl.includes("?") ? "&" : "?";
      nav(`${thenUrl}${sep}email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}&name=${encodeURIComponent(name)}`);
    }
  };

  const check = async () => {
    setErr("");
    const digits = phone.replace(/\D/g, "");
    if (!email.includes("@") && digits.length < 10) { setErr("Please enter your email or a 10-digit phone number (both is best)."); return; }
    setChecking(true);
    try {
      const { data } = await axios.get(`${API}/site/waiver/check`, { params: { email, phone } });
      if (data.signed) { setStep("already"); return; }
    } catch { /* fall through to signing */ }
    finally { setChecking(false); }
    // Only a NEW signature needs the full legal name.
    if (name.trim().length < 2) { setErr("You're not registered yet — please enter your full name to sign the waiver."); return; }
    setStep("waiver");
  };

  return (
    <div className="min-h-screen bg-grain flex items-start justify-center p-4 pt-8">
      <div className="w-full max-w-lg rounded-2xl bg-[#101318] p-6 shadow-2xl" data-testid="register-page">
        <div className="text-center mb-5">
          <div className="text-xs uppercase tracking-[0.3em] till-amber font-display mb-1">The Self-Agency Project</div>
          <h1 className="text-2xl font-extrabold font-display till-text m-0">Training Registration</h1>
          <p className="text-white/60 text-sm mt-1">One-time waiver — sign it once and you're set for all training, workshops and events.</p>
        </div>

        {step === "contact" && (
          <div data-testid="register-contact-step">
            <label className="block text-xs text-white/70 mb-1">Full name</label>
            <input className="w-full rounded-lg px-3 py-2.5 mb-3 border-0 bg-white text-[#000000]" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="register-name-input" />
            <label className="block text-xs text-white/70 mb-1">Email</label>
            <input className="w-full rounded-lg px-3 py-2.5 mb-3 border-0 bg-white text-[#000000]" value={email} inputMode="email"
              onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="register-email-input" />
            <label className="block text-xs text-white/70 mb-1">Mobile number</label>
            <input className="w-full rounded-lg px-3 py-2.5 mb-1 border-0 bg-white text-[#000000]" value={phone} inputMode="tel"
              onChange={(e) => setPhone(e.target.value)} placeholder="403-555-0123" data-testid="register-phone-input" />
            <p className="text-[11px] text-white/45 mb-3">Give both if you can — either one identifies you next time so you never sign twice.</p>
            {err && <div className="text-[#FF5100] text-xs mb-2" data-testid="register-error">{err}</div>}
            <button onClick={check} disabled={checking} data-testid="register-continue-btn"
              className="w-full py-3 rounded-full font-display font-bold uppercase tracking-wide text-sm border-0 cursor-pointer disabled:opacity-60"
              style={{ background: "#FFE01A", color: "#000000" }}>
              {checking ? "Checking…" : "Continue"}
            </button>
          </div>
        )}

        {step === "waiver" && (
          <WaiverStep name={name} email={email} phone={phone} source="walk-in" refInfo="front-desk QR"
            submitLabel="Sign & register" onSigned={() => setStep("done")} />
        )}

        {(step === "already" || step === "done") && (
          <div className="text-center py-6" data-testid={step === "already" ? "register-already" : "register-done"}>
            <CheckCircle size={64} weight="fill" color="#CCFF00" className="mx-auto" />
            <h2 className="text-xl font-extrabold font-display till-text mt-3 mb-1">
              {step === "already" ? "You're already registered!" : "You're registered!"}
            </h2>
            <p className="text-white/60 text-sm">
              {step === "already"
                ? "We found your signed waiver — no need to sign again. See you on the mats."
                : "Waiver signed and on file. You never have to sign it again — for any training, workshop or event."}
            </p>
            {thenUrl ? (
              <button onClick={proceed} data-testid="register-then-btn"
                className="mt-4 px-6 py-2.5 rounded-full font-display font-bold uppercase tracking-wide text-sm border-0 cursor-pointer"
                style={{ background: "#FFE01A", color: "#000000" }}>
                Continue to payment →
              </button>
            ) : (
              <div className="flex flex-col gap-2.5 mt-5 items-center">
                <button data-testid="register-purchase-btn"
                  onClick={() => nav(`/buy?email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}&name=${encodeURIComponent(name)}`)}
                  className="w-full max-w-xs py-3 rounded-full font-display font-bold uppercase tracking-wide text-sm border-0 cursor-pointer"
                  style={{ background: "#FFE01A", color: "#000000" }}>
                  Purchase training →
                </button>
                <a href="/" data-testid="register-home-btn"
                  className="w-full max-w-xs py-3 rounded-full font-display font-bold uppercase tracking-wide text-sm text-center no-underline border border-white/30 till-text">
                  Go to main website
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
